import React, { useContext, useEffect, useRef, useState } from "react";
import DocumentPreviewModal from "../../modals/DocumentPreviewModal";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpenText,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  HeartPulse,
  IdCard,
  ListTodo,
  Plane,
  ShieldUser,
  MoreVertical,
} from "lucide-react";
import { images } from "../../Assets/images";
import { GlobalContext } from "../../Context/GlobalContext";
import ConfirmDeleteModal from "../../modals/ConfirmDeleteModal";

const Documents = () => {
  const { translatedLabels, getDocumentList, uploadedFiles, deletedocument } =
    useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;
  const authLabel = translatedLabels?.auth;
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleExportClick = () => {
    if (!isExportMode) {
      setIsExportMode(true);
      setSelectedFiles([]);
    } else {
      if (selectedFiles.length > 0) {
        navigate("/export-document", { state: { selectedIds: selectedFiles } });
        setIsExportMode(false);
        setSelectedFiles([]);
      } else {
        setIsExportMode(false);
        setSelectedFiles([]);
      }
    }
  };

  const MAX_SIZE_KB = 50000;

  const handleCheckboxChange = (file) => {
    if (file.file_size > MAX_SIZE_KB) {
      window.showToast("error", "File exceeds 50 MB limit");
      return;
    }

    setSelectedFiles((prev) =>
      prev.includes(file.id)
        ? prev.filter((id) => id !== file.id)
        : [...prev, file.id],
    );
  };

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();

    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".dropdown-menu") && !e.target.closest(".btn")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const categoryStyles = {
    all: { icon: <ListTodo />, color: "bg-default" },
    id: { icon: <IdCard />, color: "bg-pink" },
    healthcare: { icon: <HeartPulse />, color: "bg-tan" },
    insurance: { icon: <ShieldUser />, color: "bg-yellow" },
    benefits: { icon: images.BenefitsIcon, color: "bg-blue" },
    travel: { icon: <Plane />, color: "bg-green" },
    education: { icon: <BookOpenText />, color: "bg-gray" },
  };
  useEffect(() => {
    getDocumentList();
  }, [getDocumentList]);

  const filterItemsData = [
    { id: 0, key: "all", name: screenLabels?.all?.label_value || "All" },
    { id: 1, key: "id", name: screenLabels?.id?.label_value || "ID" },
    {
      id: 2,
      key: "healthcare",
      name: screenLabels?.healthcare?.label_value || "Healthcare",
    },
    {
      id: 3,
      key: "insurance",
      name: screenLabels?.insurance?.label_value || "Insurance",
    },
    {
      id: 4,
      key: "benefits",
      name: screenLabels?.benefits?.label_value || "Benefits",
    },
    {
      id: 5,
      key: "travel",
      name: screenLabels?.travel?.label_value || "Travel",
    },
    {
      id: 6,
      key: "education",
      name: screenLabels?.education?.label_value || "Education",
    },
  ];

  const getCategoryStyle = (key) => {
    return (
      categoryStyles[key] || { icon: images.wellnessIcon, color: "bg-default" }
    );
  };

  const renderFilters = (scrollRef) => (
    <div
      className="d-flex align-items-start gap-2 overflow-auto py-2 hide-scrollbar"
      ref={scrollRef}
    >
      {filterItemsData.map((item) => {
        const { icon, color } = getCategoryStyle(item.key);
        return (
          <div className="filter-item text-center" key={item.id}>
            <button
              className={`filter-btn ${color} ${
                activeFilter === item.key ? "active" : ""
              }`}
              onClick={() => setActiveFilter(item.key)}
            >
              {typeof icon === "string" ? (
                <img
                  src={icon}
                  alt={item.key}
                  style={{ width: 34, height: 34 }}
                />
              ) : (
                icon
              )}
            </button>
            <span className="mb-0 pb-0">{item.name}</span>
          </div>
        );
      })}
    </div>
  );

  const filteredCards = uploadedFiles.filter(
    (card) => activeFilter === "all" || card.key === activeFilter,
  );

  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = 150;

    if (direction === "left") {
      container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };
  const truncateMiddle = (str, maxLength = 20) => {
    if (!str) return "";
    if (str.length <= maxLength) return str;

    const half = Math.floor((maxLength - 3) / 2);
    return str.slice(0, half) + "..." + str.slice(-half);
  };
  return (
    <>
      <div className="content-part">
        <p className="small mb-3 mb-0 d-flex gap-2 text-primary alert alert-info p-2 align-items-center">
          <i className="fa-solid fa-circle-info "></i>
          This page lets you store all your documents in one place and easily
          share them along with scan results.
        </p>
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h5>
            {screenLabels?.select_category?.label_value || "Select Category"}
          </h5>
          {isOverflowing && (
            <div className="d-flex align-items-center gap-3">
              <div
                className="previous-icon"
                onClick={() => scroll("left")}
                style={{ cursor: "pointer" }}
              >
                <ChevronLeft />
              </div>
              <div
                className="next-icon"
                onClick={() => scroll("right")}
                style={{ cursor: "pointer" }}
              >
                <ChevronRight />
              </div>
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-body">{renderFilters(scrollRef)}</div>
        </div>
        <hr />

        <div className="my-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {screenLabels?.uploaded_docs?.label_value || "Uploaded Documents"}
            </h5>
            <div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleExportClick}
              >
                {!isExportMode
                  ? authLabel?.export?.label_value || "Export"
                  : selectedFiles.length > 0
                    ? screenLabels?.done?.label_value || "Done"
                    : screenLabels?.cancel?.label_value || "Cancel"}
              </button>
            </div>
          </div>
        </div>

        <div>
          {filteredCards.length > 0 ? (
            filteredCards?.map((file) => {
              const { icon, color } = getCategoryStyle(file.key);

              return (
                <div
                  key={file.id}
                  className="card d-flex align-items-center flex-row p-2 gap-2 mb-3 position-relative cursor-pointer"
                >
                  <div className={`icon-wrapper ${color}`}>
                    {typeof icon === "string" ? (
                      <img
                        src={icon}
                        alt={file.key}
                        style={{ width: 34, height: 34 }}
                      />
                    ) : (
                      icon
                    )}
                  </div>

                  <div className="w-100">
                    <h6 className="mb-1 file-name">
                      {truncateMiddle(file.fileName, 20)}
                    </h6>
                    <div className="d-flex justify-content-between">
                      <div>
                        <p className="mb-0 text-muted fs-14px">
                          {screenLabels?.uploaded?.label_value || "Uploaded"} :{" "}
                          {file.date}{" "}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="view-button">
                        {isExportMode ? (
                          <input
                            className="big-checkbox"
                            type="checkbox"
                            checked={selectedFiles.includes(file.id)}
                            onChange={() => handleCheckboxChange(file)}
                          />
                        ) : (
                          <button
                            className="btn p-0"
                            onClick={() =>
                              setOpenMenuId(
                                openMenuId === file.id ? null : file.id,
                              )
                            }
                          >
                            <MoreVertical size={20} />
                          </button>
                        )}
                      </div>

                      {openMenuId === file.id && (
                        <div
                          className="dropdown-menu show"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "100%",
                            zIndex: 1,
                          }}
                        >
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              setSelectedFile(file);
                              setShowModal(true);
                              setOpenMenuId(null);
                            }}
                          >
                            {screenLabels?.view?.label_value || "View"}
                          </button>
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              navigate("/upload-document", {
                                state: { file, selectedCategory: file.key },
                              });
                            }}
                          >
                            {screenLabels?.edit?.label_value || "Edit"}
                          </button>
                          {/* <button className="dropdown-item">
                            {screenLabels?.share?.label_value || "Share"}
                          </button> */}
                          <button
                            className="dropdown-item text-danger"
                            onClick={() => {
                              setFileToDelete(file);
                              setShowDeleteModal(true);
                              setOpenMenuId(null);
                            }}
                          >
                            {screenLabels?.delete?.label_value || "Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-muted mt-5">
              {screenLabels?.no_docs?.label_value || "No documents available"}
            </p>
          )}
        </div>

        {selectedFile && (
          <DocumentPreviewModal
            show={showModal}
            onClose={() => setShowModal(false)}
            file={selectedFile}
          />
        )}

        <ConfirmDeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            setShowDeleteModal(false);
            deletedocument(fileToDelete?.id);
          }}
          screenLabels={screenLabels}
        />

        <div className="upload-button">
          <Link
            to={"/upload-document"}
            state={{ selectedCategory: activeFilter }}
          >
            <CloudUpload className="text-white" size={27} />
          </Link>
        </div>
      </div>
    </>
  );
};

export default Documents;
